import { FC } from 'react';
import { DIALOGS_TABLE_NAME, type ConversationListItem } from '@/entities/conversations';
import { SelectionSavePopover, TablePresetsPopover } from '@/features/TablePresets';
import { PRESETS_WIDTH_RESERVE_CLASS } from './ContentSearch/controlRowClasses';
import { DialogListDataGrid } from './DialogListDataGrid';
import { useDialogListState } from './model/useDialogListState';
import { usePresetRoute } from './model/usePresetRoute';

/** Пропсы секции списка диалогов */
export type DialogListSectionProps = {
  onLoaded?: (data: ConversationListItem[]) => void;
  /**
   * Узел вне колонки списка, в который переносится блок управления. Задают раскладки, где
   * рядом со списком стоит карточка диалога и колонке списка блоку тесно. Промежуток от
   * перенесённого блока до списка держит раскладка — по метке плотности на самом блоке.
   */
  controlsContainer?: HTMLElement | null;
  className?: string;
};

/**
 * Секция списка диалогов раздела «Диалоги» (и закреплённого пресета) на базе DataGrid.
 * Состояние списка ведёт `useDialogListState`; своё у раздела — слой отбора: пресеты таблицы
 * и выборка перехода с дашборда.
 */
export const DialogListSection: FC<DialogListSectionProps> = ({ className, controlsContainer, onLoaded }) => {
  const { gridProps, presets } = useDialogListState({ tableName: DIALOGS_TABLE_NAME, onLoaded });

  // Адрес раздела закреплённого пресета: открытый адрес применяет пресет, а смена пресета
  // в списке переводит на его адрес (или на обычный список диалогов).
  const { onActivePresetChange } = usePresetRoute(DIALOGS_TABLE_NAME);

  return (
    <DialogListDataGrid
      {...gridProps}
      className={className}
      controlsContainer={controlsContainer}
      canSaveLayer={presets.canSaveActivePreset}
      layerName={presets.activePreset?.name}
      onSaveLayer={presets.onSaveActivePreset}
      canExitLayer={presets.canDeselectSelection}
      onExitLayer={presets.onDeselectSelection}
      layerBlock={
        <SelectionSavePopover
          tableName={DIALOGS_TABLE_NAME}
          selection={presets.unsavedSelection}
          onDismiss={presets.onSelectionHintDismiss}
        >
          <TablePresetsPopover
            tableName={DIALOGS_TABLE_NAME}
            widthReserveClassName={PRESETS_WIDTH_RESERVE_CLASS}
            onActivePresetChange={onActivePresetChange}
          />
        </SelectionSavePopover>
      }
    />
  );
};
